import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { 
  Building2, User, Mail, Phone, CreditCard, ArrowRight, 
  Check, Shield, Sparkles, ArrowLeft, Loader2, Lock, AlertCircle, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL;
const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY;

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

const plans = {
  starter: { name: 'Starter', price: 19, users: '5 users' },
  growth: { name: 'Growth', price: 49, users: '20 users' },
  business: { name: 'Business', price: 99, users: 'Unlimited users' }
};

// Card Element Styles
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Inter, system-ui, sans-serif',
      '::placeholder': {
        color: '#64748b',
      },
      iconColor: '#64748b',
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
};

// Checkout Form Component
const CheckoutForm = ({ formData, plan, planId, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState(null);

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    setCardError(event.error ? event.error.message : null);
  };

  // Start trial without card (no auto-charge)
  const handleFreeTrialOnly = async () => {
    setTrialLoading(true);
    try {
      const params = new URLSearchParams({
        company_name: formData.company_name,
        admin_name: formData.admin_name,
        admin_email: formData.admin_email,
        phone: formData.phone || '',
        plan_id: planId
      });
      
      const response = await axios.post(`${API}/api/crm/subscription/start-trial?${params}`);
      
      if (response.data.success) {
        toast.success('Free trial started! Check your email for login details.');
        navigate('/crm/subscription/success', { 
          state: { 
            email: formData.admin_email,
            company: formData.company_name,
            plan: plan.name,
            trialDays: 14,
            noCard: true
          }
        });
      }
    } catch (error) {
      console.error('Trial error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start trial');
    } finally {
      setTrialLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    if (!consentChecked) {
      toast.error('Please agree to the billing terms');
      return;
    }

    if (!cardComplete) {
      toast.error('Please complete your card details');
      return;
    }

    setLoading(true);

    try {
      // Create payment method
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
        billing_details: {
          name: formData.admin_name,
          email: formData.admin_email,
        },
      });

      if (pmError) {
        throw new Error(pmError.message);
      }

      // Send to backend to create subscription with trial
      const response = await axios.post(`${API}/api/crm/subscription/create-with-trial`, {
        plan_id: planId,
        company_name: formData.company_name,
        admin_name: formData.admin_name,
        admin_email: formData.admin_email,
        phone: formData.phone,
        payment_method_id: paymentMethod.id
      });

      if (response.data.success) {
        toast.success('Trial started! Check your email for login details.');
        navigate('/crm/subscription/success', { 
          state: { 
            email: formData.admin_email,
            company: formData.company_name,
            plan: plan.name,
            trialDays: 14
          }
        });
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Account Summary */}
      <div className="bg-slate-900/50 rounded-xl p-4 space-y-3">
        <h3 className="font-medium text-white mb-3 flex items-center gap-2">
          <User className="w-4 h-4 text-blue-400" />
          Account Details
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-slate-500">Company</span>
            <p className="text-white font-medium">{formData.company_name}</p>
          </div>
          <div>
            <span className="text-slate-500">Admin</span>
            <p className="text-white font-medium">{formData.admin_name}</p>
          </div>
          <div className="col-span-2">
            <span className="text-slate-500">Email</span>
            <p className="text-white font-medium">{formData.admin_email}</p>
          </div>
        </div>
      </div>

      {/* Payment Section */}
      <div className="space-y-4">
        <h3 className="font-medium text-white flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-blue-400" />
          Payment Information
        </h3>

        {/* Card Input */}
        <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-4">
          <CardElement 
            options={cardElementOptions}
            onChange={handleCardChange}
          />
        </div>
        
        {cardError && (
          <div className="flex items-center gap-2 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            {cardError}
          </div>
        )}

        {/* Security badges */}
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <Lock className="w-3 h-3" />
            <span>256-bit SSL</span>
          </div>
          <div className="flex items-center gap-1">
            <Shield className="w-3 h-3" />
            <span>Secure Payment</span>
          </div>
        </div>
      </div>

      {/* Trial Info Box */}
      <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h4 className="font-medium text-green-400 mb-1">14-Day Free Trial</h4>
            <p className="text-sm text-slate-400">
              Your card won't be charged today. You'll have full access to all {plan.name} features for 14 days.
            </p>
          </div>
        </div>
      </div>

      {/* Consent Checkbox */}
      <label className="flex items-start gap-3 cursor-pointer group">
        <div className="relative mt-0.5">
          <input
            type="checkbox"
            checked={consentChecked}
            onChange={(e) => setConsentChecked(e.target.checked)}
            className="sr-only"
            data-testid="consent-checkbox"
          />
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
            consentChecked 
              ? 'bg-blue-500 border-blue-500' 
              : 'border-slate-600 group-hover:border-slate-500'
          }`}>
            {consentChecked && <Check className="w-3 h-3 text-white" />}
          </div>
        </div>
        <span className="text-sm text-slate-400 leading-relaxed">
          I understand that after my 14-day free trial ends, I authorize CLOUD IT to automatically charge 
          <span className="text-white font-medium"> ${plan.price}/month</span> to my card until I cancel. 
          I can cancel anytime from my account settings.
        </span>
      </label>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading || !stripe || !cardComplete || !consentChecked}
        className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="start-trial-submit"
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <Sparkles className="w-5 h-5" />
            Start Trial with Card on File
          </>
        )}
      </button>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-slate-700"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-slate-800 text-slate-500">or</span>
        </div>
      </div>

      {/* Free Trial Without Card */}
      <button
        type="button"
        onClick={handleFreeTrialOnly}
        disabled={trialLoading}
        className="w-full py-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50"
        data-testid="start-trial-no-card"
      >
        {trialLoading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <Zap className="w-5 h-5" />
            Start Free Trial (No Card Required)
          </>
        )}
      </button>
      <p className="text-center text-xs text-slate-500">
        Add payment method later to continue after trial
      </p>

      {/* Back Button */}
      <button
        type="button"
        onClick={onBack}
        className="w-full py-3 text-slate-400 hover:text-white transition-colors text-sm"
      >
        ← Back to edit details
      </button>

      {/* Trust line */}
      <p className="text-center text-xs text-slate-500">
        Cancel anytime • No charges during trial • Instant access
      </p>
    </form>
  );
};

const CRMCheckout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan') || 'growth';
  const isTrial = searchParams.get('trial') === 'true';
  const plan = plans[planId] || plans.growth;

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    company_name: '',
    admin_name: '',
    admin_email: '',
    phone: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateStep1 = () => {
    if (!formData.company_name.trim()) {
      toast.error('Company name is required');
      return false;
    }
    if (!formData.admin_name.trim()) {
      toast.error('Your name is required');
      return false;
    }
    if (!formData.admin_email.trim() || !formData.admin_email.includes('@')) {
      toast.error('Valid email is required');
      return false;
    }
    return true;
  };

  const handleContinue = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-dark)' }}>
      <Header />
      
      <div className="pt-28 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/crm/pricing')}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Pricing
          </button>

          <div className="grid md:grid-cols-5 gap-8">
            {/* Main Form - 3 columns */}
            <div className="md:col-span-3">
              <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl overflow-hidden">
                {/* Header */}
                <div className="p-6 border-b border-slate-700/50 bg-gradient-to-r from-blue-500/10 to-purple-500/10">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-green-400" />
                    <span className="text-sm text-green-400 font-medium">14-Day Free Trial • No Charge Today</span>
                  </div>
                  <h1 className="text-2xl font-bold text-white">
                    Start Your CRM {plan.name} Trial
                  </h1>
                </div>

                {/* Steps Indicator */}
                <div className="px-6 pt-6">
                  <div className="flex items-center gap-4 mb-8">
                    <div className={`flex items-center gap-2 ${step >= 1 ? 'text-blue-400' : 'text-slate-500'}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 1 ? 'bg-blue-500 text-white' : 'bg-slate-700 text-slate-400'}`}>
                        {step > 1 ? <Check className="w-4 h-4" /> : '1'}
                      </div>
                      <span className="text-sm font-medium">Your Info</span>
                    </div>
                    <div className="flex-1 h-0.5 bg-slate-700">
                      <div className={`h-full bg-blue-500 transition-all ${step > 1 ? 'w-full' : 'w-0'}`}></div>
                    </div>
                    <div className={`flex items-center gap-2 ${step >= 2 ? 'text-blue-400' : 'text-slate-500'}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 2 ? 'bg-blue-500 text-white' : 'bg-slate-700 text-slate-400'}`}>
                        2
                      </div>
                      <span className="text-sm font-medium">Payment</span>
                    </div>
                  </div>
                </div>

                {/* Step 1: Company Info */}
                {step === 1 && (
                  <div className="p-6 pt-0 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        <Building2 className="w-4 h-4 inline mr-2" />
                        Company Name *
                      </label>
                      <input
                        type="text"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        placeholder="Your Company Inc."
                        className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                        data-testid="crm-checkout-company"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        <User className="w-4 h-4 inline mr-2" />
                        Your Name *
                      </label>
                      <input
                        type="text"
                        name="admin_name"
                        value={formData.admin_name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                        data-testid="crm-checkout-name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        <Mail className="w-4 h-4 inline mr-2" />
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="admin_email"
                        value={formData.admin_email}
                        onChange={handleChange}
                        placeholder="you@company.com"
                        className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                        data-testid="crm-checkout-email"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        <Phone className="w-4 h-4 inline mr-2" />
                        Phone (Optional)
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                        data-testid="crm-checkout-phone"
                      />
                    </div>

                    <button
                      onClick={handleContinue}
                      className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all mt-6"
                      data-testid="crm-checkout-continue"
                    >
                      Continue to Payment
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {/* Step 2: Payment with Stripe */}
                {step === 2 && (
                  <div className="p-6 pt-0">
                    <Elements stripe={stripePromise}>
                      <CheckoutForm 
                        formData={formData} 
                        plan={plan} 
                        planId={planId}
                        onBack={() => setStep(1)}
                      />
                    </Elements>
                  </div>
                )}
              </div>
            </div>

            {/* Order Summary - 2 columns */}
            <div className="md:col-span-2">
              <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6 sticky top-28">
                <h2 className="text-lg font-semibold text-white mb-6">Order Summary</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Plan</span>
                    <span className="text-white font-medium">{plan.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Users</span>
                    <span className="text-white">{plan.users}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Billing</span>
                    <span className="text-white">Monthly</span>
                  </div>
                </div>

                <div className="border-t border-slate-700 pt-4 mb-6">
                  <div className="flex justify-between items-baseline mb-2">
                    <span className="text-slate-400">Due Today</span>
                    <span className="text-2xl font-bold text-green-400">$0.00</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-slate-400">After 14 days</span>
                    <div>
                      <span className="text-xl font-bold text-white">${plan.price}</span>
                      <span className="text-slate-400">/month</span>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="bg-slate-900/50 rounded-xl p-4 mb-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">Today</p>
                        <p className="text-slate-500 text-xs">Start free trial</p>
                      </div>
                    </div>
                    <div className="ml-3 w-0.5 h-6 bg-slate-700"></div>
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center">
                        <CreditCard className="w-3 h-3 text-slate-400" />
                      </div>
                      <div>
                        <p className="text-slate-400 text-sm font-medium">In 14 days</p>
                        <p className="text-slate-500 text-xs">${plan.price}/month begins</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-slate-400">What's included:</h3>
                  {['Lead Management', 'Deal Pipeline', 'Contact Database', 'Activity Timeline', 'Team Collaboration', 'Priority Support'].map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400" />
                      <span className="text-sm text-slate-300">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Trust Badges */}
                <div className="mt-6 pt-6 border-t border-slate-700 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Shield className="w-4 h-4" />
                    <span>Secure 256-bit SSL encryption</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Lock className="w-4 h-4" />
                    <span>Cancel anytime, no questions asked</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CRMCheckout;
